import { useState } from 'react';
import { Check, Copy, Tag } from 'lucide-react';
import type { CouponOffer } from '@/features/campaigns/api/couponApi';
import { formatMoney } from '@/shared/lib/format/money';

type CouponCardProps = {
  coupon: CouponOffer;
};

const DESC_PREVIEW_LENGTH = 80;

export function CouponCard({ coupon }: CouponCardProps) {
  const [copied, setCopied] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  const description =
    (coupon as unknown as { description?: string })?.description?.trim() || '';
  const descIsLong = description.length > DESC_PREVIEW_LENGTH;
  const shownDescription =
    descIsLong && !descExpanded
      ? `${description.slice(0, DESC_PREVIEW_LENGTH)}…`
      : description;

  const isActive = Boolean(coupon.isActive);
  const endTimestamp = coupon.endDate ? new Date(coupon.endDate).getTime() : NaN;
  const hasValidEndDate = Number.isFinite(endTimestamp);
  const isExpired = hasValidEndDate ? endTimestamp < Date.now() : false;
  const usable = isActive && !isExpired;

  const normalizedType = String(coupon.discountType || '').trim().toUpperCase();
  const isPercentage =
    normalizedType === 'PERCENTAGE' || normalizedType === 'PERCENT';

  const discountLabel = isPercentage
    ? `${coupon.discountValue ?? 0}%`
    : formatMoney(coupon.discountValue ?? 0);

  const expiryDate = hasValidEndDate
    ? new Date(endTimestamp).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  const minSpendFormatted =
    coupon.minSpend != null && Number(coupon.minSpend) > 0
      ? formatMoney(coupon.minSpend)
      : 'No minimum';

  const maxDiscountFormatted =
    coupon.maxSpend != null && Number(coupon.maxSpend) > 0
      ? formatMoney(coupon.maxSpend)
      : 'No limit';

  const handleCopy = async () => {
    if (!usable || !coupon.couponCode) return;
    try {
      await navigator.clipboard.writeText(coupon.couponCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard errors
    }
  };

  return (
    <article
      className={`group relative flex flex-col overflow-hidden rounded-2xl border border-[var(--dash-line)] bg-white transition ${
        usable
          ? 'shadow-[0_10px_28px_-22px_rgba(16,38,73,0.35)] hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-20px_rgba(16,38,73,0.4)]'
          : 'opacity-60 shadow-sm'
      }`}
    >
      <div
        className="h-1.5 w-full"
        style={
          usable
            ? {
                background:
                  'linear-gradient(90deg, #8E58A6 0%, #5A4EA6 25%, #145E8C 50%, #007A78 75%, #33A65B 100%)',
              }
            : { background: '#D1D5DB' }
        }
      />

      <div className="flex flex-1 flex-col p-5 text-left">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {coupon.couponImage && !imageFailed ? (
              <img
                src={coupon.couponImage}
                alt=""
                onError={() => setImageFailed(true)}
                className="h-11 w-11 shrink-0 rounded-xl border border-[var(--dash-line)] object-cover"
              />
            ) : (
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                  usable
                    ? 'bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]'
                    : 'bg-[var(--dash-soft)] text-[var(--dash-muted)]'
                }`}
              >
                <Tag className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0">
              <h3
                className="m-0 truncate text-base font-semibold text-[var(--dash-ink)]"
                title={coupon.couponName || 'Coupon'}
              >
                {coupon.couponName || 'Coupon'}
              </h3>
              <p className="m-0 mt-0.5 text-xs text-[var(--dash-muted)]">
                {isPercentage ? 'Percentage discount' : 'Flat discount'}
              </p>
            </div>
          </div>

          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              usable
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-[var(--dash-soft)] text-[var(--dash-muted)]'
            }`}
          >
            {isExpired ? 'Expired' : isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        <p
          className={`m-0 text-4xl font-bold tracking-tight ${
            usable ? 'text-[var(--dash-brand)]' : 'text-[var(--dash-muted)]'
          }`}
        >
          {discountLabel}
          <span className="ml-1.5 text-base font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
            off
          </span>
        </p>

        {description ? (
          <div className="mt-3">
            <p className="m-0 text-xs leading-relaxed text-[var(--dash-muted)]">
              {shownDescription}
            </p>
            {descIsLong ? (
              <button
                type="button"
                onClick={() => setDescExpanded((v) => !v)}
                className="mt-1 border-none bg-transparent p-0 text-[11px] font-semibold text-[var(--dash-brand)] hover:underline"
              >
                {descExpanded ? 'See less' : 'See more'}
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="mt-5 grid grid-cols-2 gap-2.5">
          <div className="rounded-xl bg-[var(--dash-soft)] px-3.5 py-3">
            <p className="m-0 text-[10px] font-semibold tracking-wider text-[var(--dash-muted)] uppercase">
              Min spend
            </p>
            <p
              className="m-0 mt-1 truncate text-sm font-semibold text-[var(--dash-ink)]"
              title={minSpendFormatted}
            >
              {minSpendFormatted}
            </p>
          </div>
          <div className="rounded-xl bg-[var(--dash-soft)] px-3.5 py-3">
            <p className="m-0 text-[10px] font-semibold tracking-wider text-[var(--dash-muted)] uppercase">
              Max discount
            </p>
            <p
              className="m-0 mt-1 truncate text-sm font-semibold text-[var(--dash-ink)]"
              title={maxDiscountFormatted}
            >
              {maxDiscountFormatted}
            </p>
          </div>
        </div>

        <p className="mt-4 mb-3 flex items-center gap-1.5 text-xs text-[var(--dash-muted)]">
          {isExpired ? 'Expired on' : 'Valid until'}{' '}
          <span
            className={`font-semibold ${
              isExpired ? 'text-red-500' : 'text-[var(--dash-ink)]'
            }`}
          >
            {expiryDate}
          </span>
        </p>

        <button
          type="button"
          disabled={!usable}
          onClick={() => void handleCopy()}
          className={`mt-auto flex w-full items-center justify-between gap-3 rounded-xl border border-dashed px-4 py-3.5 transition ${
            usable
              ? 'border-[var(--dash-brand)]/30 bg-[var(--dash-brand-soft)]/60 hover:border-[var(--dash-brand)]/50'
              : 'cursor-not-allowed border-[var(--dash-line)] bg-[var(--dash-soft)]'
          }`}
        >
          <div className="min-w-0 text-left">
            <p className="m-0 text-[10px] font-semibold tracking-wider text-[var(--dash-muted)] uppercase">
              Coupon code
            </p>
            <p
              className={`m-0 truncate text-sm font-bold tracking-[0.12em] ${
                usable ? 'text-[var(--dash-brand)]' : 'text-[var(--dash-muted)]'
              }`}
              title={coupon.couponCode || '—'}
            >
              {coupon.couponCode || '—'}
            </p>
          </div>
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-lg px-3.5 py-2 text-xs font-semibold text-white ${
              copied
                ? 'bg-emerald-500'
                : usable
                  ? 'bg-[var(--dash-brand)]'
                  : 'bg-gray-300'
            }`}
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy
              </>
            )}
          </span>
        </button>
      </div>
    </article>
  );
}
